import React, { useState } from 'react';
import { Chip, CircularProgress, IconButton, Tooltip } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useSandboxFreigaben } from '../../hooks/useSandboxFreigaben';
import { useNotification } from '../../hooks/NotificationContext';
import { extractApiError } from '../../utils/errorUtils';

interface Props {
  kind: 'audit' | 'schwachstelle';
  id: number;
  /** Fuer Findings: Audit-ID, um "ueber Audit freigegeben" zu erkennen. */
  auditId?: number | null;
  /** chip = Header (mit Text), icon = kompakt (Tabellenzeile). */
  variant?: 'chip' | 'icon';
}

/**
 * Zeigt an, ob ein Audit/Finding in die isolierte Umgebung (Restricted-Login) zum Lesen
 * freigegeben ist, und erlaubt das Umschalten. Wird im Hauptaccount an Audit und Finding
 * angezeigt, damit Freigaben jederzeit sichtbar sind.
 */
const SandboxFreigabeToggle: React.FC<Props> = ({ kind, id, auditId, variant = 'chip' }) => {
  const { isAuditShared, isFindingSharedDirectly, toggleAudit, toggleFinding } = useSandboxFreigaben();
  const { showSuccess, showError } = useNotification();
  const [busy, setBusy] = useState(false);

  const direct = kind === 'audit' ? isAuditShared(id) : isFindingSharedDirectly(id);
  const viaAudit = kind === 'schwachstelle' && isAuditShared(auditId);
  const shared = direct || viaAudit;

  const label = kind === 'audit' ? 'Audit' : 'Finding';
  const tooltip = viaAudit && !direct
    ? 'Über das Audit in der isolierten Umgebung lesbar (Freigabe am Audit aufheben, um dies zu ändern)'
    : direct
      ? `${label} ist in der isolierten Umgebung lesbar – klicken zum Aufheben`
      : `${label} in der isolierten Umgebung zum Lesen freigeben`;

  const toggle = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (viaAudit && !direct) return;
    setBusy(true);
    try {
      if (kind === 'audit') await toggleAudit(id);
      else await toggleFinding(id);
      showSuccess(direct ? 'Freigabe aufgehoben' : 'In der isolierten Umgebung freigegeben');
    } catch (err) {
      showError(extractApiError(err, 'Freigabe konnte nicht geändert werden'));
    } finally {
      setBusy(false);
    }
  };

  if (variant === 'icon') {
    return (
      <Tooltip title={tooltip}>
        <span>
          <IconButton
            size="small"
            onClick={toggle}
            disabled={busy || (viaAudit && !direct)}
            data-testid={`freigabe-toggle-${kind}-${id}`}
            sx={{ color: shared ? 'warning.main' : 'text.disabled' }}
          >
            {busy ? <CircularProgress size={14} /> : shared ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={tooltip}>
      <Chip
        size="small"
        icon={busy ? <CircularProgress size={12} /> : shared ? <VisibilityIcon /> : <VisibilityOffIcon />}
        label={shared ? (viaAudit && !direct ? 'Isolierte Umgebung: lesbar (via Audit)' : 'Isolierte Umgebung: lesbar') : 'Isolierte Umgebung: nicht freigegeben'}
        color={shared ? 'warning' : 'default'}
        variant={shared ? 'filled' : 'outlined'}
        onClick={toggle}
        disabled={busy}
        data-testid={`freigabe-toggle-${kind}-${id}`}
        sx={{ fontWeight: shared ? 700 : 500, cursor: viaAudit && !direct ? 'default' : 'pointer' }}
      />
    </Tooltip>
  );
};

export default SandboxFreigabeToggle;
